import { create } from 'zustand'
import { createJSONStorage, devtools, persist, subscribeWithSelector } from 'zustand/middleware'
import { ApolloClient, ApolloError } from '@apollo/client';
import { IEditorSettings, IShortcut, PluginConstructType, PluginType, Site, TKnowledgeBase, TUploadedFileInfo, TUser } from '../../generated/gql/graphql';
import { CURRENT_USER, GET_EDITOR_SETTINGS, GET_SITES, LIST_FILES, LIST_KNOWLEDGE_BASES } from '../graphql/query';
import { removeTypename } from '../utils/removeTypename';
import { computed } from '../utils/zustand-computed';

export interface UserAndWorkspaceStore {
  user: TUser | null,
  __developerMode: boolean,
  editorSettings: IEditorSettings,
  workspaceId: string | null,
  workspaces: Site[],
  getShortcuts: () => IShortcut[],
  isDevelopmentMode: () => boolean,
  setDeveloperMode: (value: boolean) => void,
  assets: {
    knowledgeBases: { [knowledgeBaseId: string]: TKnowledgeBase },
    files: { [fileId: string]: TUploadedFileInfo },
  }
}

const defaultShortcuts: IShortcut[] = [
  { pluginType: PluginType.AwaitMessageInput },
  { pluginType: PluginType.DisplayMessage },
  { pluginType: PluginType.AiChat },
  { pluginType: PluginType.GetConversation },
  { pluginType: PluginType.AiComplete },
  { pluginConstructorType: PluginConstructType.AiConvert },
  { pluginConstructorType: PluginConstructType.AiChoose },
  { pluginType: PluginType.Condition },
]

export const defaultEditorSettings: IEditorSettings = {
  shortcuts: defaultShortcuts
}

const defaultIdentity = {
  user: null,
  __developerMode: false,
  editorSettings: defaultEditorSettings,
  workspaceId: null,
  workspaces: [],
  assets: {
    knowledgeBases: {},
    files: {},
  },
}

export interface UserAndWorkspaceLoader {
  loadingStatus: {
    user: boolean,
    workspaces: boolean,
    editorSettings: boolean,
    knowledgeBases: boolean,
    files: boolean,
  },
  loadError: {
    user: ApolloError | null,
    workspaces: ApolloError | null,
    editorSettings: ApolloError | null,
    knowledgeBases: ApolloError | null,
    files: ApolloError | null,
  },
  isLoading: () => boolean,
  loadIdentity(client: ApolloClient<object>, loadWorkspaces?: boolean): Promise<TUser | null>,
  loadEditorSettings(client: ApolloClient<object>): Promise<void>,
  loadWorkspaceAssets(client: ApolloClient<object>): Promise<void>,
}

export const useUserAndWorkspaceStore = create<UserAndWorkspaceStore & UserAndWorkspaceLoader>()(
  devtools(persist(subscribeWithSelector((set, get) => ({
    getShortcuts: () => {
      let shortcuts = get().editorSettings?.shortcuts;
      return shortcuts?.length ? shortcuts : defaultShortcuts;
    },
    loadIdentity: async (client: ApolloClient<object>, shouldLoadWorkspaces: boolean) => {
      if (get().user) {
        // Skip loading if user is already loaded
        // we do this to avoid unnecessary network requests
        // and to prevent workspace information being reset
        return get().user;
      }
      const u = await loadUser(client, get, set);
      if (u) {
        await loadEditorSettings(client, get, set);

        if (shouldLoadWorkspaces) {
          await loadWorkspaces(client, get, set)
        }
      }
      return u;
    },
    loadEditorSettings: async (client) => {
      await loadEditorSettings(client, get, set);
    },
    loadWorkspaceAssets: async (client) => {
      const workspaceId = get().workspaceId;
      loadWorkspaceAssets(client, workspaceId, get, set);
    },
    loadingStatus: {
      user: false,
      workspaces: false,
      editorSettings: false,
      knowledgeBases: false,
      files: false,
    },
    isLoading: () => {
      return get().loadingStatus.user || get().loadingStatus.workspaces || get().loadingStatus.editorSettings;
    },
    loadError: {
      user: null,
      workspaces: null,
      editorSettings: null,
      knowledgeBases: null,
      files: null,
    },
    isDevelopmentMode: computed(
      () => [get().user?.isDeveloper, get().__developerMode],
      ([], isDeveloper, devMode) => isDeveloper && devMode,
    ),
    setDeveloperMode: (value: boolean) => set({ __developerMode: value }),
    error: undefined,
    ...defaultIdentity,
  })),
    {
      name: 'go-pixie-identity-storage', // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => sessionStorage), // (optional) by default, 'localStorage' is used
    },
  ))
)

async function loadEditorSettings(
  client: ApolloClient<object>,
  get: () => UserAndWorkspaceLoader & UserAndWorkspaceStore,
  set: (updates: Partial<UserAndWorkspaceLoader & UserAndWorkspaceStore>) => void,
): Promise<void> {
  set({ loadingStatus: { ...get().loadingStatus, editorSettings: true } });
  const { data: loadEditorSettings, error: loadEditorSettingsError } = await client.query({
    query: GET_EDITOR_SETTINGS,
    fetchPolicy: 'no-cache',
  });
  if (loadEditorSettingsError) {
    set({ loadError: { ...get().loadError, editorSettings: loadEditorSettingsError } });
  }
  else if (loadEditorSettings.editorSettings?.shortcuts?.length) {
    set({ editorSettings: removeTypename(loadEditorSettings.editorSettings) });
  }
  else {
    set({ editorSettings: defaultEditorSettings });
  }
  set({ loadingStatus: { ...get().loadingStatus, editorSettings: false } });
}

async function loadWorkspaces(
  client: ApolloClient<object>,
  get: () => UserAndWorkspaceLoader & UserAndWorkspaceStore,
  set: (updates: Partial<UserAndWorkspaceLoader & UserAndWorkspaceStore>) => void,
): Promise<void> {
  set({ loadingStatus: { ...get().loadingStatus, workspaces: true } });
  const { data: loadWorkspace, error: loadWorkspaceError } = await client.query({
    query: GET_SITES,
    fetchPolicy: 'no-cache',
  });
  if (loadWorkspaceError) {
    set({
      loadError: { ...get().loadError, workspaces: loadWorkspaceError },
      workspaceId: null,
      workspaces: [],
      assets: {
        knowledgeBases: {},
        files: {},
      }
    });
  } else {
    const currentWorkspaceId = get().workspaceId;
    const workspaceId = loadWorkspace.sites.find(s => s.id === currentWorkspaceId) ? currentWorkspaceId : loadWorkspace.sites[0].id || null;
    await loadWorkspaceAssets(client, workspaceId, get, set);
    set({ workspaces: loadWorkspace.sites, workspaceId });
  }
  set({ loadingStatus: { ...get().loadingStatus, workspaces: false } });
}

async function loadUser(
  client: ApolloClient<object>,
  get: () => UserAndWorkspaceLoader & UserAndWorkspaceStore,
  set: (updates: Partial<UserAndWorkspaceLoader & UserAndWorkspaceStore>) => void,
): Promise<TUser | null> {
  set({ loadingStatus: { ...get().loadingStatus, user: true } });
  const { data: loadUser, error: loadUserError } = await client.query({
    query: CURRENT_USER,
    fetchPolicy: 'no-cache',
  });
  if (loadUserError) {
    set({ loadError: { ...get().loadError, user: loadUserError } });
    set({ loadingStatus: { ...get().loadingStatus, user: false } });
    return null;
  }
  set({ user: loadUser.user || null, workspaces: [], workspaceId: null });
  set({ loadingStatus: { ...get().loadingStatus, user: false } });
  return loadUser.user;
}

async function loadWorkspaceAssets(
  client: ApolloClient<object>,
  workspaceId: string | null,
  get: () => UserAndWorkspaceLoader & UserAndWorkspaceStore,
  set: (updates: Partial<UserAndWorkspaceLoader & UserAndWorkspaceStore>) => void,
): Promise<void> {
  // Reset assets if no workspace is selected
  if (!workspaceId) {
    set({
      assets: {
        knowledgeBases: {},
        files: {}
      }
    });
    return;
  }

  // Update loading statuses
  set({
    loadingStatus: {
      ...get().loadingStatus,
      knowledgeBases: true,
      files: true
    }
  });

  // Load knowledge bases
  try {
    const { data: knowledgeBasesData, error: knowledgeBasesError } = await client.query({
      query: LIST_KNOWLEDGE_BASES,
      variables: { workspaceId },
      fetchPolicy: 'no-cache',
    });

    if (knowledgeBasesError) {
      set({ loadError: { ...get().loadError, knowledgeBases: knowledgeBasesError } });
    } else {
      // Convert array to object with ID as keys
      const knowledgeBasesMap = {};
      knowledgeBasesData.alistKnowledgeBases.results.forEach((kb) => {
        knowledgeBasesMap[kb.id] = removeTypename(kb);
      });

      set({
        assets: {
          ...get().assets,
          knowledgeBases: knowledgeBasesMap
        }
      });
    }
  } catch (error) {
    set({
      loadError: {
        ...get().loadError,
        knowledgeBases: error instanceof ApolloError ? error : new ApolloError({ errorMessage: String(error) })
      }
    });
  } finally {
    set({ loadingStatus: { ...get().loadingStatus, knowledgeBases: false } });
  }

  // Load files
  try {
    const { data: filesData, error: filesError } = await client.query({
      query: LIST_FILES,
      variables: { siteId: workspaceId },
      fetchPolicy: 'no-cache',
    });

    if (filesError) {
      set({ loadError: { ...get().loadError, files: filesError } });
    } else {
      // Convert array to object with ID as keys
      const filesMap = {};
      filesData.files.forEach((file) => {
        filesMap[file.id] = removeTypename(file);
      });

      set({
        assets: {
          ...get().assets,
          files: filesMap
        }
      });
    }
  } catch (error) {
    set({
      loadError: {
        ...get().loadError,
        files: error instanceof ApolloError ? error : new ApolloError({ errorMessage: String(error) })
      }
    });
  } finally {
    set({ loadingStatus: { ...get().loadingStatus, files: false } });
  }
}
